import setIsContextualFormatBarShown from '../actions/setIsContextualFormatBarShown';
import { lazyFormatPainterFinish, lazyShowInsertLinkDialog, lazyUpdateRibbonState } from '../index';
import RibbonFormatState from '../store/schema/RibbonFormatState';
import RibbonViewState from '../store/schema/RibbonViewState';
import { PluginUtils, PluginViewStateAdapter } from 'owa-editor';
import { lazyCloseExpressionPane } from 'owa-expressions-store';
import setWordToReplace from 'owa-expressions-store/lib/actions/setWordToReplace';
import expressionStore from 'owa-expressions-store/lib/store/expressionStore';
import { lazyShowExpressionPane } from 'owa-expressions-view';
import { isBrowserFirefox } from 'owa-user-agent';
import { isMac } from 'owa-user-agent/lib/userAgent';
import { ContentEditFeature, Editor, EditorPlugin } from 'roosterjs-editor-core';
import { ContentPosition, PluginEvent, PluginEventType } from 'roosterjs-editor-types';

const KEY_ESC = 27;
const KEY_K = 75;

const hasCtrlOrMetaKey: (event: KeyboardEvent) => boolean = isMac()
    ? e => e.metaKey
    : e => e.ctrlKey;

export default class RibbonPlugin implements EditorPlugin {
    private editor: Editor;
    private pluginUtils: PluginUtils;
    private updateRibbonState: (
        plugin: RibbonPlugin,
        viewState: RibbonViewState,
        updateContextualFormatBar: boolean,
        delay: boolean
    ) => void;

    constructor(
        private adapter: PluginViewStateAdapter<RibbonViewState>,
        private getSelectedImageCallback?: Promise<() => HTMLImageElement>
    ) {}

    public getName() {
        return 'Ribbon';
    }

    public initialize(editor: Editor): void {
        this.editor = editor;
        this.editor.addContentEditFeature(this.insertLinkFeature);
        this.editor.addContentEditFeature(this.closeContextualFormatBarFeature);
        lazyUpdateRibbonState
            .import()
            .then(updateRibbonState => (this.updateRibbonState = updateRibbonState));
    }

    public onPluginEvent(event: PluginEvent): void {
        const ribbonViewState = this.adapter();

        if (
            this.updateRibbonState &&
            ((event.eventType == PluginEventType.KeyUp && event.rawEvent.keyCode != KEY_ESC) ||
                event.eventType == PluginEventType.MouseUp ||
                event.eventType == PluginEventType.ContentChanged)
        ) {
            let updateContextFormatBar = event.eventType != PluginEventType.ContentChanged;
            this.updateRibbonState(this, ribbonViewState, updateContextFormatBar, true /*delay*/);
        }

        if (
            event.eventType == PluginEventType.MouseUp &&
            ribbonViewState.formatViewState.formatPainterFormat
        ) {
            // NOTE: When the editor is disposed, we trigger the plugin event "BeforeDispose",
            // so check the event type first (VSO 54192)
            lazyFormatPainterFinish.import().then(formatPainterFinish => formatPainterFinish(this));
        }
    }

    public dispose(): void {
        this.editor = null;
        lazyCloseExpressionPane.importAndExecute();
    }

    public getEditor(): Editor {
        return this.editor;
    }

    public getFormatState(): RibbonFormatState {
        return this.adapter().formatViewState;
    }

    public async getImageSelectedByPlugin(): Promise<HTMLImageElement> {
        return this.getSelectedImageCallback ? (await this.getSelectedImageCallback)() : null;
    }

    public setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    /**
     * There's an issue in firefox that focus will be invisible after a Fabric dialog box is shown if focus was in editor originally
     * To workaround this issue, we insert a hidden textbox and let it grab the focus before showing dialog box
     */
    public workaroundFocusIssueForFirefox = isBrowserFirefox()
        ? () => {
              if (!this.editor) {
                  return;
              }
              let workaroundTextbox = this.editor.getCustomData(
                  'WORKAROUND_TEXTBOX_FOR_FIREFOX',
                  () => {
                      let textbox = this.editor.getDocument().createElement('input');
                      textbox.type = 'textbox';
                      textbox.style.width = '0';
                      textbox.style.height = '0';
                      this.editor.insertNode(textbox, {
                          position: ContentPosition.Outside,
                          updateCursor: false,
                          replaceSelection: false,
                          insertOnNewLine: false,
                      });
                      return textbox;
                  },
                  textbox => textbox.parentNode && textbox.parentNode.removeChild(textbox)
              );
              workaroundTextbox.style.display = '';
              workaroundTextbox.focus();
              workaroundTextbox.style.display = 'none';
          }
        : () => {};

    public toggleExpressionPicker(openExpressionPicker: boolean, useFlexPane: boolean) {
        if (openExpressionPicker) {
            lazyShowExpressionPane.importAndExecute(
                this.insertNodeFromExpressionPicker,
                useFlexPane,
                '',
                'ribbon'
            );
        } else {
            lazyCloseExpressionPane.importAndExecute();
        }
    }

    public insertNodeFromExpressionPicker = (node: HTMLElement) => {
        if (this.editor && !this.editor.isDisposed()) {
            this.editor.focus();

            const wordToReplace = expressionStore.expressionPickerViewState.wordToReplace;
            const insertNodeCallback = () => {
                if (wordToReplace) {
                    this.pluginUtils.replaceWithNode(
                        this.editor,
                        wordToReplace,
                        node,
                        true /* exactMatch */
                    );
                } else {
                    this.editor.insertNode(node, {
                        position: ContentPosition.SelectionStart,
                        updateCursor: true,
                        replaceSelection: true,
                        insertOnNewLine: false,
                    });
                }
                setWordToReplace(expressionStore.expressionPickerViewState, '');
            };
            this.editor.performAutoComplete(insertNodeCallback, 'Ribbon');
        }
    };

    private insertLinkFeature: ContentEditFeature = {
        keys: [KEY_K],
        allowFunctionKeys: true,
        shouldHandleEvent: event => {
            const rawEvent = event.rawEvent;
            return !rawEvent.altKey && !rawEvent.shiftKey && hasCtrlOrMetaKey(rawEvent);
        },
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            this.workaroundFocusIssueForFirefox();
            lazyShowInsertLinkDialog
                .import()
                .then(showInsertLinkDialog => showInsertLinkDialog(this));
        },
    };

    private closeContextualFormatBarFeature: ContentEditFeature = {
        keys: [KEY_ESC],
        shouldHandleEvent: () => this.adapter().isContextualFormatBarShown,
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            setIsContextualFormatBarShown(this.adapter(), false /*isShown*/);
        },
    };
}
